<script setup>
import { computed, getCurrentInstance, ref, useAttrs } from 'vue';

import HrSelect from '../primitives/HrSelect.vue';
import HrListItem from './HrListItem.vue';

/**
 * Text field that suggests as you type — M3's exposed dropdown menu in its editable variant.
 *
 * IT IS A FIELD, NOT A SELECT, and that is the whole reason it is its own component. Its value is the
 * text someone typed, which may be something no option offers; a select's value is the option they
 * chose. Folding both into one component behind a flag would make `modelValue` change shape according
 * to another prop, which is the same reason `HrMultiSelect` is separate from `HrListSelect`.
 *
 * Choosing a suggestion writes that option's label into the field, so it arrives as an ordinary
 * `update:modelValue`. `select` fires alongside it carrying the option's value, for a caller that
 * wants more from the choice than the words it left behind.
 *
 * Options are never narrowed here. `filter` asks the caller to do it, and the list stays as it was
 * until they answer, so the same event serves both a local search and one that has to go and fetch.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Someone may type any value, and past or common ones are worth offering
 * @useWhen A field's suggestions have to be fetched before they can be shown
 * @avoidWhen The value must be one of the options — use HrListSelect
 * @avoidWhen More than one value can be chosen — use HrMultiSelect
 * @avoidWhen There is nothing to suggest — use HrTextField
 * @related HrListSelect
 * @related HrTextField
 * @a11y The field is a text input; suggestions are reachable by arrow keys and chosen with Enter.
 */
defineOptions({ name: 'HrAutocompleteField', inheritAttrs: false });

/**
 * @typedef {{ label: string, value: *, caption?: string, icon?: string, disable?: boolean }}
 *   HrAutocompleteFieldOption
 */

const props = defineProps({
  /** The text in the field. */
  modelValue: {
    type: String,
    default: null,
  },
  /**
   * What to suggest, in the order they should appear. Narrow them in response to `filter`.
   * @type {HrAutocompleteFieldOption[]}
   * @example [{"label":"Morning routine","value":"a"},{"label":"Afternoon session","value":"b"}]
   */
  options: {
    type: Array,
    default: () => [],
  },
  /** Field label. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Supporting text below the field: one sentence the reader needs to fill it in.
   * A longer or occasional explanation goes behind an HrIconHintBtn, never in a tooltip.
   */
  hint: {
    type: String,
    default: null,
  },
  /** Validation rule functions; each receives the current text and returns true or an error string. */
  rules: {
    type: Array,
    default: null,
  },
  /** Delays validation until the field loses focus. */
  lazyRules: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the field. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Shows a loading indicator, for while suggestions are being fetched. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Applies error styling. */
  error: {
    type: Boolean,
    default: false,
  },
  /** Error text shown below the field when `error` is true. */
  errorMessage: {
    type: String,
    default: null,
  },
  /** Hides the bottom space reserved for hint and error text. */
  hideBottomSpace: {
    type: Boolean,
    default: false,
  },
  /** Reduces vertical padding for compact layouts. */
  dense: {
    type: Boolean,
    default: false,
  },
  /**
   * Milliseconds to wait before asking for narrowed options. Leave at 0 for a list already in memory;
   * raise it when `filter` goes to the network.
   */
  filterDebounce: {
    type: [String, Number],
    default: 0,
  },
});

const emit = defineEmits({
  /** Emitted as the text changes, whether it was typed or came from choosing a suggestion. */
  'update:modelValue': null,
  /** Emitted when a suggestion is chosen; payload is that option's value. */
  select: null,
  /**
   * Asks the caller to narrow `options`; receives (value, update, abort) and the list does not change
   * until `update` runs. It also fires once as the menu opens, so suggestions that come from a query
   * can be fetched on first use rather than up front.
   */
  filter: null,
});

const attrs = useAttrs();
const instance = getCurrentInstance();
const fieldRef = ref(null);

function forwardFilter(...args) {
  emit('filter', ...args);
}

/**
 * The filter listener, but only when the caller is listening for it.
 *
 * QSelect reads a filter listener as "this caller narrows the options": opening the menu starts a
 * filter, and the field stays loading with no menu until the caller answers with `update`. Passing
 * one on regardless would hang a field whose caller narrows nothing. The caller's own listener is
 * not in `useAttrs`, which drops the listeners of declared emits, and it is read per render so
 * attaching or dropping one takes effect.
 */
function filterListeners() {
  return instance.vnode.props?.onFilter ? { filter: forwardFilter } : {};
}

defineExpose({
  /** Runs the field's validation rules; returns a boolean or a Promise resolving to one. */
  validate: (...args) => fieldRef.value?.validate(...args),
});

/**
 * `hideSelected` and `fillInput` are what make the trigger read as a text field: the chosen option is
 * never drawn as a selection, and its label is written into the input instead. Without them the text
 * and the selection would both be visible and disagree the moment either changed.
 */
const selectProps = computed(() => ({
  modelValue: props.modelValue,
  options: props.options,
  optionLabel: 'label',
  optionValue: 'value',
  emitValue: true,
  useInput: true,
  hideSelected: true,
  fillInput: true,
  inputDebounce: props.filterDebounce,
  ...(props.label !== null && { label: props.label }),
  ...(props.hint !== null && { hint: props.hint }),
  ...(props.errorMessage !== null && { errorMessage: props.errorMessage }),
  ...(props.rules !== null && { rules: props.rules }),
  lazyRules: props.lazyRules,
  disable: props.disable,
  loading: props.loading,
  error: props.error,
  hideBottomSpace: props.hideBottomSpace,
  dense: props.dense,
  ...attrs,
}));
</script>

<template>
  <hr-select
    ref="fieldRef"
    v-bind="selectProps"
    v-on="filterListeners()"
    @update:model-value="emit('select', $event)"
    @input-value="emit('update:modelValue', $event)"
  >
    <!--
      Rendered rather than left to Quasar's default, which shows the label alone. The option type
      here has always documented `caption` and `icon`, and a suggestion list is exactly where they
      earn their keep: the label is often a code, and the caption is what says what the code MEANS.
      Matches how HrMultiSelect renders the same option shape, so one options array reads the same
      in either control.
    -->
    <template #option="scope">
      <hr-list-item
        v-bind="scope.itemProps"
        :label="scope.opt.label ?? null"
        :caption="scope.opt.caption ?? null"
        :icon="scope.opt.icon ?? null"
      />
    </template>
  </hr-select>
</template>
